// client/src/components/catalogoModelo/CatalogoModeloForm.js
import React, { useState, useEffect } from 'react';
import { Save, XCircle, Loader2, Image as ImageIcon, X } from 'lucide-react';
import { useMutateCatalogoModelo } from '../../hooks/useCatalogoModelo';
import { useSegmentos } from '../../hooks/useSegmentos';
import GaleriaBuscaModal from '../galeria/GaleriaBuscaModal';

const initialState = {
    nome: '',
    descricao: '',
    tipo: 'servico',
    categoriaNome: '',
    segmento: '',
    precoSugerido: '',
    duracaoSugeridaMinutos: '',
    estoqueSugerido: '',
    ativo: true
};

const CatalogoModeloForm = ({ current, onCloseForm }) => {
    const { createItem, updateItem } = useMutateCatalogoModelo();
    const { data: segmentos, isLoading: isLoadingSegmentos } = useSegmentos();
    const isMutating = createItem.isPending || updateItem.isPending;

    const [form, setForm] = useState(initialState);
    const [imagemSelecionada, setImagemSelecionada] = useState(null);
    const [showGaleriaModal, setShowGaleriaModal] = useState(false);

    useEffect(() => {
        if (current) {
            setForm({
                nome: current.nome || '',
                descricao: current.descricao || '',
                tipo: current.tipo || 'servico',
                categoriaNome: current.categoriaNome || '',
                segmento: current.segmento?._id || current.segmento || '',
                precoSugerido: current.precoSugerido ?? '',
                duracaoSugeridaMinutos: current.duracaoSugeridaMinutos ?? '',
                estoqueSugerido: current.estoqueSugerido ?? '',
                ativo: current.ativo !== undefined ? current.ativo : true
            });
            setImagemSelecionada(current.imagemGaleria || null);
        } else {
            setForm(initialState);
            setImagemSelecionada(null);
        }
    }, [current]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();

        const payload = {
            nome: form.nome,
            descricao: form.descricao || undefined,
            tipo: form.tipo,
            categoriaNome: form.categoriaNome,
            segmento: form.segmento,
            precoSugerido: form.precoSugerido !== '' ? Number(form.precoSugerido) : undefined,
            duracaoSugeridaMinutos: form.tipo === 'servico' && form.duracaoSugeridaMinutos !== '' ? Number(form.duracaoSugeridaMinutos) : undefined,
            estoqueSugerido: form.tipo === 'produto' && form.estoqueSugerido !== '' ? Number(form.estoqueSugerido) : undefined,
            imagemGaleria: imagemSelecionada?._id || undefined,
            ativo: form.ativo
        };

        if (current) {
            updateItem.mutate({ id: current._id, payload }, { onSuccess: onCloseForm });
        } else {
            createItem.mutate(payload, { onSuccess: onCloseForm });
        }
    };

    return (
        <form onSubmit={onSubmit}>
            <div className="form-section">
                <h4 className="form-section-title">{current ? 'Editar Item do Catálogo' : 'Novo Item do Catálogo'}</h4>
                <div className="form-grid-container-cols-2">
                    <div className="form-group">
                        <label className="form-label required">Nome</label>
                        <input name="nome" value={form.nome} onChange={onChange} className="form-input" placeholder="Ex.: Corte de Cabelo Masculino" required />
                    </div>
                    <div className="form-group">
                        <label className="form-label required">Tipo</label>
                        <select name="tipo" value={form.tipo} onChange={onChange} className="form-input" required>
                            <option value="servico">Serviço</option>
                            <option value="produto">Produto</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label required">Categoria</label>
                        <input name="categoriaNome" value={form.categoriaNome} onChange={onChange} className="form-input" placeholder="Ex.: Cortes" required />
                    </div>
                    <div className="form-group">
                        <label className="form-label required">Segmento</label>
                        <select name="segmento" value={form.segmento} onChange={onChange} className="form-input" required disabled={isLoadingSegmentos}>
                            <option value="">Selecione...</option>
                            {segmentos?.map((segmento) => (
                                <option key={segmento._id} value={segmento._id}>{segmento.nome}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Preço Sugerido (R$)</label>
                        <input type="number" step="0.01" min="0" name="precoSugerido" value={form.precoSugerido} onChange={onChange} className="form-input" placeholder="Opcional" />
                    </div>
                    {form.tipo === 'servico' ? (
                        <div className="form-group">
                            <label className="form-label">Duração Sugerida (minutos)</label>
                            <input type="number" min="1" name="duracaoSugeridaMinutos" value={form.duracaoSugeridaMinutos} onChange={onChange} className="form-input" placeholder="Opcional" />
                        </div>
                    ) : (
                        <div className="form-group">
                            <label className="form-label">Estoque Sugerido</label>
                            <input type="number" min="0" name="estoqueSugerido" value={form.estoqueSugerido} onChange={onChange} className="form-input" placeholder="Opcional" />
                        </div>
                    )}
                    <div className="form-group form-group--full-width">
                        <label className="form-label">Descrição</label>
                        <textarea name="descricao" value={form.descricao} onChange={onChange} className="form-input" rows={3} maxLength={500} />
                    </div>
                    <div className="form-group form-group--full-width">
                        <label className="form-label">Imagem</label>
                        {imagemSelecionada ? (
                            <div className="gallery-item-card" style={{ maxWidth: 220 }}>
                                <div className="gallery-item-card__image-container">
                                    <img src={imagemSelecionada.url} alt={imagemSelecionada.nome} />
                                </div>
                                <div className="gallery-item-card__body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h5 title={imagemSelecionada.nome}>{imagemSelecionada.nome}</h5>
                                    <button type="button" onClick={() => setImagemSelecionada(null)} className="action-button-icon text-red-600" title="Remover imagem">
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button type="button" onClick={() => setShowGaleriaModal(true)} className="action-button action-button-secondary">
                                <ImageIcon size={18} className="icon" /> Selecionar da Galeria
                            </button>
                        )}
                    </div>
                    <div className="form-group-checkbox">
                        <input type="checkbox" name="ativo" id="catalogo-modelo-ativo" checked={form.ativo} onChange={onChange} className="form-checkbox" />
                        <label htmlFor="catalogo-modelo-ativo">Item ativo (visível para importação)</label>
                    </div>
                </div>
            </div>

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary">
                    <XCircle size={18} className="icon" />Cancelar
                </button>
                <button type="submit" disabled={isMutating} className="action-button action-button-primary">
                    {isMutating ? <Loader2 size={18} className="icon animate-spin" /> : <Save size={18} className="icon" />}
                    {current ? 'Atualizar' : 'Criar Item'}
                </button>
            </div>

            {showGaleriaModal && (
                <GaleriaBuscaModal
                    onClose={() => setShowGaleriaModal(false)}
                    onImageSelect={(img) => {
                        setImagemSelecionada(img);
                        setShowGaleriaModal(false);
                    }}
                />
            )}
        </form>
    );
};

export default CatalogoModeloForm;
